import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Plus, Edit3, Trash2, X, ToggleLeft, ToggleRight, ArrowUp, ArrowDown, Layers } from 'lucide-react';
import { servicesApi } from '../../api/services';

const AR = { direction: 'rtl', textAlign: 'right' };
const EN = { direction: 'ltr', textAlign: 'left', background: '#f0fdf4', borderColor: '#86efac' };

const LABEL_KEY = 'ayonk_services_labels_v2';

const DEFAULT_LABELS = {
  pageTitleAr: 'خدماتنا', pageTitleEn: 'Our Services',
  pageSubAr: 'حلول شاملة لجميع احتياجاتك', pageSubEn: 'Comprehensive solutions for all your needs',
  servicesLabelAr: 'ما نقدمه', servicesLabelEn: 'What We Offer'
};

export function ServicesManagement() {
  const { language } = useLanguage();
  const isRtl = language === 'ar';

  const [services, setServices] = useState([]);
  const [loadingSvc, setLoadingSvc] = useState(true);
  const [savingSvc, setSavingSvc] = useState(false);
  const [svcModal, setSvcModal] = useState(false);
  const [selSvc, setSelSvc] = useState(null);
  const [svcForm, setSvcForm] = useState({ titleAr: '', titleEn: '', descAr: '', descEn: '', order: 0, isActive: true });
  const [svcErrors, setSvcErrors] = useState({});

  const [labels, setLabels] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LABEL_KEY)) || DEFAULT_LABELS; } catch { return DEFAULT_LABELS; }
  });
  const [labelSaved, setLabelSaved] = useState(false);

  const [tab, setTab] = useState('labels');

  const fetchServices = async () => {
    try { 
      setLoadingSvc(true); 
      const d = await servicesApi.getAll(); 
      setServices(Array.isArray(d) ? d.sort((a,b)=>a.order-b.order) : []); 
    } catch(e) { console.error(e); } 
    finally { setLoadingSvc(false); }
  };

  useEffect(() => { fetchServices(); }, []);
  useEffect(() => { localStorage.setItem(LABEL_KEY, JSON.stringify(labels)); }, [labels]);

  const openSvcModal = (s = null) => {
    setSvcErrors({});
    setSelSvc(s);
    if (s) {
      setSvcForm({
        titleAr: s.title?.split('|')[0]?.trim() || '',
        titleEn: s.title?.split('|')[1]?.trim() || '',
        descAr: s.description?.split('|')[0]?.trim() || '',
        descEn: s.description?.split('|')[1]?.trim() || '',
        order: s.order || 0,
        isActive: s.isActive
      });
    } else {
      setSvcForm({ titleAr: '', titleEn: '', descAr: '', descEn: '', order: services.length, isActive: true });
    }
    setSvcModal(true);
  };

  const saveSvc = async (e) => {
    e.preventDefault();
    
    const errs = {};
    if (!svcForm.titleAr.trim()) errs.titleAr = true;
    if (!svcForm.titleEn.trim()) errs.titleEn = true;
    if (!svcForm.descAr.trim()) errs.descAr = true;
    if (!svcForm.descEn.trim()) errs.descEn = true;

    if (Object.keys(errs).length > 0) {
      setSvcErrors(errs);
      return;
    }

    setSavingSvc(true);
    try {
      const payload = {
        title: `${svcForm.titleAr.trim()} | ${svcForm.titleEn.trim()}`,
        description: `${svcForm.descAr.trim()} | ${svcForm.descEn.trim()}`,
        order: svcForm.order,
        isActive: svcForm.isActive
      };

      if (selSvc) await servicesApi.update(selSvc.id, payload);
      else await servicesApi.create(payload);
      
      await fetchServices(); 
      setSvcModal(false);
    } catch(e) { 
      console.error(e); 
      alert(isRtl ? 'حدث خطأ أثناء الحفظ' : 'Error saving service'); 
    } finally { 
      setSavingSvc(false); 
    }
  };

  const deleteSvc = async (id) => {
    if (!window.confirm(isRtl ? 'حذف الخدمة؟' : 'Delete?')) return;
    try { await servicesApi.delete(id); await fetchServices(); } catch(e) { console.error(e); }
  };

  const toggleSvc = async (s) => {
    const updatedStatus = !s.isActive;
    setServices(prev => prev.map(item => item.id === s.id ? { ...item, isActive: updatedStatus } : item));
    try { 
      await servicesApi.update(s.id, { ...s, isActive: updatedStatus }); 
    } catch(e) { 
      console.error(e); 
      alert(isRtl ? 'فشل تحديث الحالة' : 'Failed to update status');
      setServices(prev => prev.map(item => item.id === s.id ? { ...item, isActive: s.isActive } : item));
    }
  };

  const reorderSvc = async (idx, dir) => {
    const arr = [...services];
    const swap = dir==='up' ? idx-1 : idx+1;
    if (swap<0||swap>=arr.length) return;
    [arr[idx], arr[swap]] = [arr[swap], arr[idx]];
    
    setServices(arr); 
    
    try {
      await servicesApi.update(arr[idx].id, {...arr[idx], order: idx});
      await servicesApi.update(arr[swap].id, {...arr[swap], order: swap});
    } catch(e) { console.error(e); await fetchServices(); }
  };

  const saveLabels = () => { setLabelSaved(true); setTimeout(()=>setLabelSaved(false), 2000); };

  const TABS = [
    { key: 'labels', label: isRtl ? '📝 تسميات الصفحة' : '📝 Page Labels' },
    { key: 'services', label: isRtl ? '⚙️ قائمة الخدمات' : '⚙️ Services List' }
  ];

  const tabStyle = (k) => ({
    padding: '12px 24px', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 14,
    borderBottom: tab===k ? '3px solid #ef4444' : '3px solid transparent',
    color: tab===k ? '#ef4444' : '#64748b', background: 'none', transition: 'all 0.2s'
  });

  const getInputStyle = (isArabic, hasError) => ({
    ...(isArabic ? AR : EN),
    borderColor: hasError ? '#ef4444' : '#e2e8f0',
    borderWidth: hasError ? '2px' : '1.5px',
    outline: 'none',
    width: '100%', padding: '10px', borderRadius: '6px'
  });

  return (
    <div className="admin-page-content" dir={isRtl?'rtl':'ltr'}>
      <header className="admin-page-header">
        <div>
          <h1>{isRtl ? 'إدارة الخدمات' : 'Services Management'}</h1>
          <p style={{color:'#64748b',fontSize:14}}>{isRtl ? 'إدارة خدمات الشركة' : 'Manage company services'}</p>
        </div>
      </header>

      <div style={{display:'flex', gap:8, borderBottom:'1px solid #e2e8f0', marginBottom:24, background:'white', borderRadius:'12px 12px 0 0', padding:'0 16px', overflowX:'auto'}}>
        {TABS.map(t => <button key={t.key} style={tabStyle(t.key)} onClick={()=>setTab(t.key)}>{t.label}</button>)}
      </div>

      {tab==='labels' && (
        <div className="admin-content-card" style={{padding: '24px'}}>
          <h3 style={{fontWeight:800, marginBottom:20}}>{isRtl?'تسميات وعناوين الصفحة':'Page Titles & Labels'}</h3>

          <div style={{background:'#f8fafc', borderRadius:8, padding:20, marginBottom:20, border: '1px solid #e2e8f0'}}>
            <h4 style={{fontWeight:700, marginBottom:16, color:'#ef4444'}}>🏠 {isRtl?'عنوان الصفحة الرئيسي':'Main Page Title'}</h4>
            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:16}}>
              <div className="form-field">
                <label>العنوان الرئيسي (عربي)</label>
                <input style={getInputStyle(true)} value={labels.pageTitleAr} onChange={e=>setLabels({...labels,pageTitleAr:e.target.value.replace(/[a-zA-Z]/g, '')})} />
              </div>
              <div className="form-field">
                <label style={{color:'#16a34a'}}>Main Title (English)</label>
                <input style={getInputStyle(false)} value={labels.pageTitleEn} onChange={e=>setLabels({...labels,pageTitleEn:e.target.value.replace(/[\u0600-\u06FF]/g, '')})} />
              </div>
              <div className="form-field">
                <label>العنوان الفرعي (عربي)</label>
                <input style={getInputStyle(true)} value={labels.pageSubAr} onChange={e=>setLabels({...labels,pageSubAr:e.target.value.replace(/[a-zA-Z]/g, '')})} />
              </div>
              <div className="form-field">
                <label style={{color:'#16a34a'}}>Subtitle (English)</label>
                <input style={getInputStyle(false)} value={labels.pageSubEn} onChange={e=>setLabels({...labels,pageSubEn:e.target.value.replace(/[\u0600-\u06FF]/g, '')})} />
              </div>
            </div>
          </div>

          <div style={{background:'#f8fafc', borderRadius:8, padding:20, marginBottom:20, border: '1px solid #e2e8f0'}}>
            <h4 style={{fontWeight:700, marginBottom:16, color:'#6366f1'}}>⚙️ {isRtl?'قسم الخدمات':'Services Section'}</h4>
            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:16}}>
              <div className="form-field">
                <label>تسمية القسم (عربي)</label>
                <input style={getInputStyle(true)} value={labels.servicesLabelAr} onChange={e=>setLabels({...labels,servicesLabelAr:e.target.value.replace(/[a-zA-Z]/g, '')})} />
              </div>
              <div className="form-field">
                <label style={{color:'#16a34a'}}>Section Label (English)</label>
                <input style={getInputStyle(false)} value={labels.servicesLabelEn} onChange={e=>setLabels({...labels,servicesLabelEn:e.target.value.replace(/[\u0600-\u06FF]/g, '')})} />
              </div>
            </div>
          </div>

          <button onClick={saveLabels} className="btn-add-primary" style={{padding: '12px 32px'}}>
            {labelSaved ? '✅ ' + (isRtl?'تم الحفظ!':'Saved!') : (isRtl?'💾 حفظ التسميات':'💾 Save Labels')}
          </button>
        </div>
      )}

      {tab==='services' && (
        <div className="admin-content-card">
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding: '24px 24px 0'}}>
            <h3 style={{fontWeight:800}}>{isRtl?'قائمة الخدمات':'Services List'} <span style={{color:'#94a3b8', fontWeight:400, fontSize:14}}>({services.length})</span></h3>
            <button className="btn-add-primary" onClick={()=>openSvcModal()}><Plus size={18}/><span>{isRtl?'إضافة خدمة':'Add Service'}</span></button>
          </div>

          {loadingSvc ? <p style={{textAlign:'center',padding:40}}>Loading...</p> :
          services.length===0 ? (
            <div style={{textAlign:'center', padding:60}}>
              <Layers size={48} color="#e2e8f0" style={{margin:'0 auto 16px'}}/>
              <p style={{color:'#94a3b8'}}>{isRtl?'لا توجد خدمات بعد. انقر على إضافة لبدء العمل.':'No services yet. Click Add to begin.'}</p>
            </div>
          ) : (
            <div style={{padding: '24px'}}>
              <table className="admin-table">
                <thead><tr>
                  <th>#</th>
                  <th>{isRtl?'الخدمة':'Service'}</th>
                  <th>{isRtl?'الوصف':'Description'}</th>
                  <th>{isRtl?'الحالة':'Status'}</th>
                  <th>{isRtl?'الترتيب':'Order'}</th>
                  <th>{isRtl?'إجراءات':'Actions'}</th>
                </tr></thead>
                <tbody>
                  {services.map((s,idx)=>(
                    <tr key={s.id}>
                      <td style={{color:'#94a3b8',fontWeight:700}}>{idx+1}</td>
                      <td style={{fontWeight:700}}>{s.title?.split('|')[0] || s.title}</td>
                      <td style={{color:'#64748b',fontSize:13,maxWidth:200}}>
                        <div style={{whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',maxWidth:200}}>{s.description?.split('|')[0] || s.description || '—'}</div>
                      </td>
                      <td>
                        <button onClick={()=>toggleSvc(s)} style={{background:'none',border:'none',cursor:'pointer',display:'flex',alignItems:'center',gap:6, padding: '4px 8px', borderRadius: '6px', backgroundColor: s.isActive ? '#ecfdf5' : '#f8fafc', transition: 'all 0.2s'}}>
                          {s.isActive
                            ? <><ToggleRight size={24} color="#10b981"/><span style={{color:'#10b981',fontSize:12,fontWeight:700}}>{isRtl?'نشط':'Active'}</span></>
                            : <><ToggleLeft size={24} color="#94a3b8"/><span style={{color:'#94a3b8',fontSize:12,fontWeight:700}}>{isRtl?'معطل':'Inactive'}</span></>
                          }
                        </button>
                      </td>
                      <td>
                        <div style={{display:'flex',gap:4}}>
                          <button onClick={()=>reorderSvc(idx,'up')} disabled={idx===0} style={{background:'none',border:'1px solid #e2e8f0',borderRadius:4,padding:'4px 6px',cursor:'pointer',opacity:idx===0?0.3:1}}><ArrowUp size={13}/></button>
                          <button onClick={()=>reorderSvc(idx,'down')} disabled={idx===services.length-1} style={{background:'none',border:'1px solid #e2e8f0',borderRadius:4,padding:'4px 6px',cursor:'pointer',opacity:idx===services.length-1?0.3:1}}><ArrowDown size={13}/></button>
                        </div>
                      </td>
                      <td className="actions-cell">
                        <button className="btn-icon edit" onClick={()=>openSvcModal(s)}><Edit3 size={16}/></button>
                        <button className="btn-icon delete" onClick={()=>deleteSvc(s.id)}><Trash2 size={16}/></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {svcModal && (
        <div className="modal-overlay">
          <div className="modal-container" style={{maxWidth:700}}>
            <div className="modal-header" style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: 16, marginBottom: 20 }}>
              <h2>{selSvc?(isRtl?'تعديل خدمة':'Edit Service'):(isRtl?'خدمة جديدة':'New Service')}</h2>
              <button className="modal-close" onClick={()=>setSvcModal(false)}><X size={24}/></button>
            </div>
            
            {Object.keys(svcErrors).length > 0 && (
              <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', color: '#ef4444', padding: '12px', borderRadius: '8px', marginBottom: '20px', fontSize: '14px', textAlign: 'center', fontWeight: '600' }}>
                {isRtl ? 'يرجى ملء جميع الحقول المطلوبة' : 'Please fill all required fields'}
              </div>
            )}

            <form className="admin-form" onSubmit={saveSvc}>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:24}}>
                
                {/* ARABIC SIDE */}
                <div style={{display: 'flex', flexDirection: 'column', gap: 16}}>
                  <div className="form-field">
                    <label>اسم الخدمة (عربي) <span style={{color:'#ef4444'}}>*</span></label>
                    {/* THE FIX: Regex replace English letters from Arabic fields */}
                    <input style={getInputStyle(true, svcErrors.titleAr)} value={svcForm.titleAr} onChange={e=>{setSvcForm({...svcForm,titleAr:e.target.value.replace(/[a-zA-Z]/g, '')}); if(svcErrors.titleAr) setSvcErrors({...svcErrors, titleAr: null});}} placeholder="أعمال التشطيب" />
                  </div>
                  <div className="form-field">
                    <label>الوصف (عربي) <span style={{color:'#ef4444'}}>*</span></label>
                    <textarea rows="3" style={getInputStyle(true, svcErrors.descAr)} value={svcForm.descAr} onChange={e=>{setSvcForm({...svcForm,descAr:e.target.value.replace(/[a-zA-Z]/g, '')}); if(svcErrors.descAr) setSvcErrors({...svcErrors, descAr: null});}} placeholder="وصف الخدمة..." />
                  </div>
                </div>

                {/* ENGLISH SIDE */}
                <div style={{display: 'flex', flexDirection: 'column', gap: 16}}>
                  <div className="form-field">
                    <label style={{color:'#16a34a'}}>Service Name (English) <span style={{color:'#ef4444'}}>*</span></label>
                    {/* THE FIX: Regex replace Arabic letters from English fields */}
                    <input style={getInputStyle(false, svcErrors.titleEn)} value={svcForm.titleEn} onChange={e=>{setSvcForm({...svcForm,titleEn:e.target.value.replace(/[\u0600-\u06FF]/g, '')}); if(svcErrors.titleEn) setSvcErrors({...svcErrors, titleEn: null});}} placeholder="Finishing Works" />
                  </div>
                  <div className="form-field">
                    <label style={{color:'#16a34a'}}>Description (English) <span style={{color:'#ef4444'}}>*</span></label>
                    <textarea rows="3" style={getInputStyle(false, svcErrors.descEn)} value={svcForm.descEn} onChange={e=>{setSvcForm({...svcForm,descEn:e.target.value.replace(/[\u0600-\u06FF]/g, '')}); if(svcErrors.descEn) setSvcErrors({...svcErrors, descEn: null});}} placeholder="Service description..." />
                  </div>
                </div>

                {/* SETTINGS */}
                <div className="form-field" style={{ gridColumn: '1 / span 1' }}>
                  <label>{isRtl?'الترتيب':'Order'}</label>
                  <input type="number" min="0" style={{...getInputStyle(isRtl, false), direction: 'ltr', textAlign: isRtl ? 'right' : 'left'}} value={svcForm.order} onChange={e=>setSvcForm({...svcForm,order:parseInt(e.target.value)})} />
                </div>
                <div className="form-field" style={{ gridColumn: '2 / span 1' }}>
                  <label>{isRtl?'الحالة':'Status'}</label>
                  <select style={{...getInputStyle(isRtl, false), direction: isRtl ? 'rtl' : 'ltr'}} value={svcForm.isActive} onChange={e=>setSvcForm({...svcForm,isActive:e.target.value==='true'})}>
                    <option value="true">{isRtl?'نشط':'Active'}</option>
                    <option value="false">{isRtl?'معطل':'Inactive'}</option>
                  </select>
                </div>

              </div>
              <div className="modal-footer" style={{ borderTop: '1px solid #e2e8f0', paddingTop: 20, marginTop: 20 }}>
                <button type="button" className="btn-cancel" onClick={()=>setSvcModal(false)}>{isRtl?'إلغاء':'Cancel'}</button>
                <button type="submit" className="btn-add-primary" disabled={savingSvc}>{savingSvc?'...':(isRtl?'حفظ الخدمة':'Save Service')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
